import React, { FC, useState, useCallback, useEffect, useRef } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Pagination } from "@/components/elements/Pagination";
import { IconFileImport } from "@/assets/icons";
import {
  useRecentOfferAcceptedPaginated,
  exportTalentCsvToVisaApi,
  exportCompanyCsvToVisaApi,
  exportTalentCsvToVisaBulkApi,
  exportCompanyCsvToVisaBulkApi,
  fetchRecentOfferAcceptedClassificationBulkApi,
} from "@/api/services/main/dashboard";
import { TRecentOfferAcceptedItem, TRecentOfferAcceptedClassificationItem } from "@/api/services/main/dashboard/dtos/recent-offer-accepted.dto";
import { CaseMatchStatusOptions, EXPIRY_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { background, button as buttonColors, divider, text, state } from "@/theme/colors";
import { CsvExportModal } from "@/features/dashboard/components/CsvExportModal";
import { useTableSelection } from "@/features/dashboard/hooks/useTableSelection";
import { truncateWithEllipsis } from "@/utils/string";
import { showError } from "@/utils/error";
import { CaseMatchStatusTag } from "@/features/talent/components/talent-common/CaseMatchStatusTag";
import { findLabelByValue } from "@/utils/object";
import { usePagination } from "@/hooks/usePagination";
import { AppRoutes } from "@/routes/config";
import { EMPTY_STR } from "@/constants/string";
import { TalentAvatarCell } from "@/components/commons/TalentAvatarCell";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { hasClassificationMismatch, hasCompanyClassificationMismatch, getExpiryStatus } from "@/features/case/utils/caseMatchHelper";
import { DATE_FORMAT } from "@/constants/datetime";
import { saveAs } from "file-saver";
import {
  CLASSIFICATION_MISMATCH_MESSAGE,
  RESIDENCE_EXPIRING_SOON_MESSAGE,
  RESIDENCE_EXPIRED_MESSAGE,
  PASSPORT_EXPIRING_SOON_MESSAGE,
  PASSPORT_EXPIRED_MESSAGE,
} from "@/features/case/utils/messageWarning";

const PER_PAGE = 20;
const MAX_TEXT_LENGTH = 14;

const formatDate = (date: string | null | undefined): string => {
  if (!date) return EMPTY_STR.TEXT;
  return dayjs(date).format(DATE_FORMAT);
};

export const OfferAcceptedListPage: FC = () => {
  const navigate = useNavigate();
  const { page: urlPage } = usePagination();
  const currentPage = urlPage ?? 1;

  const { data: result, isLoading } = useRecentOfferAcceptedPaginated(currentPage, PER_PAGE, true);
  const items = result?.data?.items ?? [];
  const pagination = result?.data?.pagination;
  const totalCount = pagination?.total ?? 0;

  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvModalMode, setCsvModalMode] = useState<"single" | "bulk">("single");
  const [csvTargetItem, setCsvTargetItem] = useState<TRecentOfferAcceptedItem | null>(null);
  const [resolvingSelection, setResolvingSelection] = useState(false);
  const [itemsById, setItemsById] = useState<Map<number, TRecentOfferAcceptedClassificationItem>>(new Map());
  const [modalSelectedItems, setModalSelectedItems] = useState<TRecentOfferAcceptedClassificationItem[]>([]);
  // Tracks whether itemsById already holds every item in the current dataset (not just the
  // pages the user has paginated through) — set after a full fetch, invalidated whenever
  // totalCount changes since that means the underlying dataset itself changed.
  const [hasFullDataset, setHasFullDataset] = useState(false);
  const selection = useTableSelection();

  const countItem = Math.min((pagination?.count ?? 0) + PER_PAGE * currentPage - PER_PAGE, totalCount);

  const formatTotal = (total: number) => total.toLocaleString("en-US");

  useEffect(() => {
    if (items.length === 0) return;
    setItemsById((prev) => {
      const next = new Map(prev);
      items.forEach((item) => next.set(item.id, item));
      return next;
    });
  }, [items]);

  const prevTotalCountRef = useRef(totalCount);
  useEffect(() => {
    if (prevTotalCountRef.current !== totalCount) {
      prevTotalCountRef.current = totalCount;
      setHasFullDataset(false);
    }
  }, [totalCount]);

  const handleCsvClick = useCallback((e: React.MouseEvent, item: TRecentOfferAcceptedItem) => {
    e.stopPropagation();
    setCsvTargetItem(item);
    setCsvModalMode("single");
    setCsvModalOpen(true);
  }, []);

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Resolve full item data for the current selection lazily, only when actually needed (export click) —
  // mirrors Talent's delete flow (fetch-all-at-action-time), not the select-all click itself.
  const resolveSelectedItems = useCallback(async (): Promise<TRecentOfferAcceptedClassificationItem[]> => {
    if (selection.isAllMode) {
      if (!hasFullDataset) {
        const res = await fetchRecentOfferAcceptedClassificationBulkApi({ isAll: true, caseMatchIds: [] });
        const allItems = res.data?.items ?? [];
        setItemsById((prev) => {
          const next = new Map(prev);
          allItems.forEach((item) => next.set(item.id, item));
          return next;
        });
        setHasFullDataset(true);
        return allItems.filter((item) => !selection.excludedIds.has(item.id));
      }
      return Array.from(itemsById.values()).filter((item) => !selection.excludedIds.has(item.id));
    }

    // Unlike isAllMode, an explicit selection can resolve by fetching only the ids not
    // already cached (IN clause), instead of pulling the whole dataset just to filter it.
    const missingIds = Array.from(selection.includedIds).filter((id) => !itemsById.has(id));
    if (missingIds.length > 0) {
      const res = await fetchRecentOfferAcceptedClassificationBulkApi({ isAll: false, caseMatchIds: missingIds });
      const fetchedItems = res.data?.items ?? [];
      const merged = new Map(itemsById);
      fetchedItems.forEach((item) => merged.set(item.id, item));
      setItemsById(merged);
      return Array.from(selection.includedIds)
        .map((id) => merged.get(id))
        .filter((item): item is TRecentOfferAcceptedClassificationItem => !!item);
    }

    return Array.from(selection.includedIds)
      .map((id) => itemsById.get(id))
      .filter((item): item is TRecentOfferAcceptedClassificationItem => !!item);
  }, [selection.isAllMode, selection.includedIds, selection.excludedIds, itemsById, hasFullDataset]);

  const handleBulkCsvClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        setResolvingSelection(true);
        const resolved = await resolveSelectedItems();
        setModalSelectedItems(resolved);
        setCsvModalMode("bulk");
        setCsvModalOpen(true);
      } catch (err) {
        showError(err);
      } finally {
        setResolvingSelection(false);
      }
    },
    [resolveSelectedItems],
  );

  const handleRowClick = useCallback((item: TRecentOfferAcceptedItem) => {
    if (!item.case?.job?.id || !item.case?.id) return;
    window.open(AppRoutes.caseEdit.replace(":jobId", String(item.case.job.id)).replace(":caseId", String(item.case.id)), "_blank");
  }, []);

  const showSingleClassificationWarning = csvTargetItem
    ? hasClassificationMismatch(
        csvTargetItem.talent?.specificBusinessClassificationIds,
        csvTargetItem.case?.job?.companySpecificBusinessClassificationIds,
      )
    : false;
  const showSingleCompanyClassificationWarning = csvTargetItem
    ? hasCompanyClassificationMismatch(
        csvTargetItem.case?.job?.company?.industryClassification?.companySpecificIndustryId1,
        csvTargetItem.case?.job?.company?.industryClassification?.companySpecificBusinessClassificationIds1,
        csvTargetItem.case?.job?.companySpecificIndustryId,
        csvTargetItem.case?.job?.companySpecificBusinessClassificationIds,
      )
    : false;
  const showBulkClassificationWarning = modalSelectedItems.some((item) =>
    hasClassificationMismatch(item.talent?.specificBusinessClassificationIds, item.case?.job?.companySpecificBusinessClassificationIds),
  );
  const showBulkCompanyClassificationWarning = modalSelectedItems.some((item) =>
    hasCompanyClassificationMismatch(
      item.case?.job?.company?.industryClassification?.companySpecificIndustryId1,
      item.case?.job?.company?.industryClassification?.companySpecificBusinessClassificationIds1,
      item.case?.job?.companySpecificIndustryId,
      item.case?.job?.companySpecificBusinessClassificationIds,
    ),
  );

  const handleExportCompany = useCallback(async () => {
    try {
      if (csvModalMode === "bulk") {
        const payload = selection.isAllMode
          ? { isAll: true, caseMatchIds: Array.from(selection.excludedIds) }
          : { isAll: false, caseMatchIds: Array.from(selection.includedIds) };
        const { blob, fileName } = await exportCompanyCsvToVisaBulkApi(payload);
        saveAs(blob, fileName);
        return;
      }
      if (!csvTargetItem) return;
      await exportCompanyCsvToVisaApi(csvTargetItem.id, csvTargetItem.case?.job?.company?.companyName);
    } catch (e) {
      showError(e);
    }
  }, [csvModalMode, selection.isAllMode, selection.excludedIds, selection.includedIds, csvTargetItem]);

  const handleExportTalent = useCallback(async () => {
    try {
      if (csvModalMode === "bulk") {
        const payload = selection.isAllMode
          ? { isAll: true, caseMatchIds: Array.from(selection.excludedIds) }
          : { isAll: false, caseMatchIds: Array.from(selection.includedIds) };
        const { blob, fileName } = await exportTalentCsvToVisaBulkApi(payload);
        saveAs(blob, fileName);
        return;
      }
      if (!csvTargetItem) return;
      await exportTalentCsvToVisaApi(csvTargetItem.id, csvTargetItem.talent?.fullName);
    } catch (e) {
      showError(e);
    }
  }, [csvModalMode, selection.isAllMode, selection.excludedIds, selection.includedIds, csvTargetItem]);

  return (
    <Stack gap={2}>
      <TopPage title="内定受諾者一覧" enablePageBack={false} />
      <InnerBox padding="20px 24px 24px" showDivider={false} loading={isLoading} sx={{ flex: 1 }}>
        <Stack direction="row" mb={2}>
          <Typography variant="body14Semi" color={text.primary}>
            全 {formatTotal(totalCount)} 件中 {countItem} 件を表示
          </Typography>
        </Stack>

        <TableContainer>
          <TableHeader>
            <HeaderCell sx={{ width: "40px" }}>
              <Checkbox
                nonControl
                checked={selection.isAllMode}
                indeterminate={!selection.isAllMode && items.length > 0 && items.every((item) => selection.isSelected(item.id))}
                disabled={totalCount === 0}
                onClick={stopPropagation}
                onChange={() => selection.toggleSelectAll()}
              />
            </HeaderCell>
            <HeaderCell sx={{ width: "116px" }}>ステータス</HeaderCell>
            <HeaderCell sx={{ flex: 1 }}>氏名</HeaderCell>
            <HeaderCell sx={{ flex: 1 }}>呼び名</HeaderCell>
            <HeaderCell sx={{ flex: 1 }}>内定先企業名</HeaderCell>
            <HeaderCell sx={{ flex: 1 }}>求人名</HeaderCell>
            <HeaderCell sx={{ width: "110px" }}>内定受諾日</HeaderCell>
            <HeaderCell sx={{ width: "106px" }}>入社日</HeaderCell>
            <HeaderCell sx={{ width: "146px" }}>データ移行</HeaderCell>
          </TableHeader>

          {items.length === 0 && !isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body14" color={text.tertiary} fontWeight={700}>
                データがありません。
              </Typography>
            </Box>
          ) : (
            items.map((item, index) => {
              const isLastItem = index === items.length - 1;
              const showMismatchWarning = hasClassificationMismatch(
                item.talent?.specificBusinessClassificationIds,
                item.case?.job?.companySpecificBusinessClassificationIds,
              );
              const residenceExpiry = getExpiryStatus(item.talent?.periodOfStay);
              const passportExpiry = getExpiryStatus(item.talent?.passportExpiryDate);

              return (
                <RowWrapper
                  key={item.id}
                  $isLast={isLastItem}
                  onClick={() => handleRowClick(item)}
                  sx={{ cursor: "pointer", "&:hover": { backgroundColor: background.primary } }}
                >
                  <TableRow>
                    <RowCell sx={{ width: "40px" }}>
                      <Checkbox
                        nonControl
                        checked={selection.isSelected(item.id)}
                        onClick={stopPropagation}
                        onChange={() => selection.toggle(item.id)}
                      />
                    </RowCell>
                    <RowCell sx={{ width: "116px" }}>
                      <CaseMatchStatusTag status={item.status} title={findLabelByValue(CaseMatchStatusOptions, item.status)} variant="bold" />
                    </RowCell>
                    <RowCell sx={{ flex: 1, gap: 1 }}>
                      <TalentAvatarCell filePath={item.talent?.avatarThumbnailPath} name={item.talent?.fullName} size={28} />
                      {item.talent?.fullName ? truncateWithEllipsis(item.talent.fullName, 18) : EMPTY_STR.TEXT}
                    </RowCell>
                    <RowCell sx={{ flex: 1 }}>
                      {item.talent?.nickName ? truncateWithEllipsis(item.talent.nickName, MAX_TEXT_LENGTH) : EMPTY_STR.TEXT}
                    </RowCell>
                    <RowCell sx={{ flex: 1 }}>
                      {item.case?.job?.company?.companyName
                        ? truncateWithEllipsis(item.case.job.company.companyName, MAX_TEXT_LENGTH)
                        : EMPTY_STR.TEXT}
                    </RowCell>
                    <RowCell sx={{ flex: 1 }}>
                      {item.case?.job?.jobName ? truncateWithEllipsis(item.case.job.jobName, MAX_TEXT_LENGTH) : EMPTY_STR.TEXT}
                    </RowCell>
                    <RowCell sx={{ width: "110px" }}>{formatDate(item.offerAcceptDate)}</RowCell>
                    <RowCell sx={{ width: "106px" }}>{formatDate(item.companyJoiningDate)}</RowCell>
                    <RowCell sx={{ width: "146px" }}>
                      <Button
                        variant="outline"
                        size="sm"
                        startIcon={<IconFileImport color={buttonColors.primary} width={20} height={20} />}
                        onClick={(e) => handleCsvClick(e, item)}
                      >
                        CSV取得
                      </Button>
                    </RowCell>
                  </TableRow>
                  {residenceExpiry && (
                    <WarningRow>{residenceExpiry === EXPIRY_STATUS.EXPIRED ? RESIDENCE_EXPIRED_MESSAGE : RESIDENCE_EXPIRING_SOON_MESSAGE}</WarningRow>
                  )}
                  {passportExpiry && (
                    <WarningRow>{passportExpiry === EXPIRY_STATUS.EXPIRED ? PASSPORT_EXPIRED_MESSAGE : PASSPORT_EXPIRING_SOON_MESSAGE}</WarningRow>
                  )}
                  {showMismatchWarning && <WarningRow>{CLASSIFICATION_MISMATCH_MESSAGE}</WarningRow>}
                </RowWrapper>
              );
            })
          )}
        </TableContainer>

        <Stack mt={3}>
          <Pagination totalPage={pagination?.totalPage ?? 1} />
        </Stack>
      </InnerBox>

      <Box mt={2}>
        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" size="sm" onClick={() => navigate(AppRoutes.home)}>
              戻る
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selection.selectedCount(totalCount) === 0 || resolvingSelection}
              onClick={handleBulkCsvClick}
            >
              チェックしたCSVをダウンロード
            </Button>
          </Stack>
        </FooterContainer>
      </Box>

      <CsvExportModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        showClassificationWarning={csvModalMode === "bulk" ? showBulkClassificationWarning : showSingleClassificationWarning}
        showCompanyClassificationWarning={csvModalMode === "bulk" ? showBulkCompanyClassificationWarning : showSingleCompanyClassificationWarning}
        onExportCompany={handleExportCompany}
        onExportTalent={handleExportTalent}
      />
    </Stack>
  );
};

const TableContainer = styled(Box)`
  border: 1px solid ${divider.middle};
  border-radius: 8px;
  overflow-y: auto;
  max-height: calc(100vh - 527px);

  .MuiCheckbox-root {
    width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
  }
`;

const TableHeader = styled(Stack)`
  position: sticky;
  top: 0;
  z-index: 1;
  flex-direction: row;
  align-items: center;
  height: 40px;
  box-sizing: border-box;
  background-color: ${background.primary};
  border-bottom: 1px solid ${divider.middle};
  padding: 10px 16px;
  gap: 8px;
`;

const HeaderCell = styled((props: React.ComponentProps<typeof Typography>) => <Typography variant="body14Bold" {...props} />)`
  color: ${text.primary};
  white-space: nowrap;
`;

const RowWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isLast",
})<{ $isLast: boolean }>`
  box-shadow: ${(props) => (props.$isLast ? "none" : `inset 0 -1px ${divider.middle}`)};
`;

const TableRow = styled(Stack)`
  flex-direction: row;
  align-items: center;
  height: 42px;
  box-sizing: border-box;
  padding: 0 16px;
  gap: 8px;
`;

const RowCell = styled(Box)`
  font-size: 14px;
  font-weight: 400;
  color: ${text.primary};
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WarningRow = styled(Box)`
  padding: 0 16px 8px;
  font-size: 12px;
  color: ${state.warning_1};
`;
